import React, { useState } from "react";
import { Clock, Calendar, Globe, CheckCircle, Video, ArrowLeft } from "react-feather";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import logo from "../../assets/images/landingpage/logo.png";
import { showToast } from "../../components/ToastifyNotification";

// ✅ NEW
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { sendMail } from "../../api/mailAPI";

// ✅ Validation Schema (no option removed)
const schema = yup.object().shape({
    doctorName: yup.string().required("Doctor/Hospital Name is required"),
    speciality: yup.string().required("Speciality is required"),
    mobile: yup
        .string()
        .required("Mobile number is required")
        .matches(/^[0-9]{10}$/, "Enter valid 10 digit mobile number"),
    businessType: yup.string().required("Business Type is required"),
    // email: yup
    //     .string()
    //     .required("Email is required")
    //     .email("Invalid email format"),
    website: yup.string().nullable(),
    message: yup.string().nullable(),
});

const BookingModule = () => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState("date");
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);

    // ✅ React Hook Form
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema)
    });

    // ✅ Time Slots (UNCHANGED)
    const generateTimeSlots = () => {
        const slots = [];
        let start = 10 * 60;
        let end = 18 * 60 + 30;

        while (start <= end) {
            const hours = Math.floor(start / 60);
            const minutes = start % 60;

            const ampm = hours >= 12 ? "PM" : "AM";
            const formattedHour = hours % 12 === 0 ? 12 : hours % 12;

            slots.push(`${formattedHour}:${minutes.toString().padStart(2, "0")} ${ampm}`);
            start += 30;
        }

        return slots;
    };

    const times = generateTimeSlots();

    const isToday = selectedDate?.toDateString() === new Date().toDateString();

    const isPastTime = (time) => {
        if (!isToday) return false;

        const [t, modifier] = time.split(" ");
        let [hours, minutes] = t.split(":").map(Number);

        if (modifier === "PM" && hours !== 12) hours += 12;
        if (modifier === "AM" && hours === 12) hours = 0;

        const now = new Date();
        const selected = new Date();
        selected.setHours(hours, minutes, 0);

        return selected < now;
    };

    // ✅ Submit
    const onSubmit = async (data) => {
        if (!selectedDate || !selectedTime) {
            showToast("error", "Please select date & time");
            return;
        }

        setLoading(true);

        const emailBody = `
        <h3>New Booking Received</h3>
        <p><b>Doctor/Hospital:</b> ${data.doctorName}</p>
        <p><b>Speciality:</b> ${data.speciality}</p>
        <p><b>Mobile:</b> ${data.mobile}</p>
        <p><b>Business Type:</b> ${data.businessType}</p>
        <p><b>Email:</b> ${data.email}</p>
        <p><b>Website:</b> ${data.website}</p>
        <p><b>Message:</b> ${data.message}</p>
        <p><b>Date:</b> ${selectedDate?.toDateString()}</p>
        <p><b>Time:</b> ${selectedTime}</p>
        `;

        try {
            const response = await sendMail({
                subject: "New Booking Received",
                html: emailBody,
            });

            if (response.success) {
                showToast("success", "Booking successful!");
                setStep("success");
            } else {
                showToast("error", "Booking failed!");
            }
        } catch (error) {
            showToast("error", "Something went wrong!");
        }finally {
            setLoading(false);
        }
    };

    return (
        <section className="py-5">
            <div className="container booking-wrapper">
                <div className="card border-0 rounded-4 border-5 border-top border-danger">
                    <div className="card-body p-4">
                        <div className="row booking-card">

                            {/* LEFT PANEL */}
                            <div className="col-md-4 border-end position-relative mb-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (step === "form") setStep("date");
                                        else window.history.back();
                                    }}
                                    className="d-flex justify-content-center align-items-center rounded-circle shadow-sm bg-white text-dark border-0"
                                    style={{ width: "45px", height: "45px" }}
                                >
                                    <ArrowLeft size={20} />
                                </button>

                                <img src={logo} alt="logo" className="logo-st mt-3 mb-1" />

                                <h4 className="fw-bold mt-3 mb-4">
                                    <span className="lanp-primary-text">AZSM</span> ENTERPRISES
                                </h4>

                                <div className="d-flex align-items-center mt-3">
                                    <Clock size={16} className="me-2" />
                                    <small>30 min</small>
                                </div>

                                <div className="d-flex align-items-center mt-3 mb-3">
                                    <Video size={16} className="me-2" />
                                    <small>Web conferencing details provided upon confirmation.</small>
                                </div>

                                {selectedDate && selectedTime && (
                                    <>
                                        <div className="d-flex align-items-center mt-2">
                                            <Calendar size={16} className="me-2" />
                                            <small>{selectedTime}, {selectedDate.toDateString()}</small>
                                        </div>

                                        <div className="d-flex align-items-center mt-2">
                                            <Globe size={16} className="me-2" />
                                            <small>India Standard Time</small>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* RIGHT PANEL */}
                            <div className="col-md-8 mt-4">

                                {/* STEP 1 */}
                                {step === "date" && (
                                    <div className="row">
                                        <div className={selectedDate ? "col-md-6" : "col-12"}>
                                            <h5>Select a Date & Time</h5>

                                            <DatePicker
                                                selected={selectedDate}
                                                onChange={(date) => {
                                                    setSelectedDate(date);
                                                    setSelectedTime(null);
                                                }}
                                                minDate={new Date()}
                                                inline
                                            />
                                        </div>

                                        {selectedDate && (
                                            <div className="col-md-6 mt-4 mt-md-0">
                                                <h6>{selectedDate.toDateString()}</h6>

                                                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                                                    {times.map((time) => (
                                                        <button
                                                            key={time}
                                                            type="button"
                                                            disabled={isPastTime(time)}
                                                            className={`btn w-100 mb-2 ${selectedTime === time
                                                                    ? "btn-danger"
                                                                    : "btn-outline-danger"
                                                                }`}
                                                            onClick={() => setSelectedTime(time)}
                                                        >
                                                            {time}
                                                        </button>
                                                    ))}
                                                </div>

                                                {selectedTime && (
                                                    <button
                                                        type="button"
                                                        className="btn lanp-btn-primary w-100 text-white mt-2 rounded-pill"
                                                        onClick={() => setStep("form")}
                                                    >
                                                        Next
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* STEP 2 FORM */}
                                {step === "form" && (
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <div className="row">

                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Doctor/Hospital Name</label>
                                                <input
                                                    {...register("doctorName")}
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Doctor/Hospital Name"
                                                />
                                                <p className="text-danger">{errors.doctorName?.message}</p>
                                            </div>

                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Speciality</label>
                                                <select {...register("speciality")} className="form-select">
                                                    <option value="">Please Select</option>
                                                    <option>Speciality</option>
                                                    <option>Multi-Specialty</option>
                                                    <option>Cardiology</option>
                                                    <option>Dermatology & Cosmetic</option>
                                                    <option>Neurology</option>
                                                    <option>Family Practice</option>
                                                    <option>Urology</option>
                                                </select>
                                                <p className="text-danger">{errors.speciality?.message}</p>
                                            </div>

                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Mobile Number</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">+91</span>
                                                    <input
                                                        {...register("mobile")}
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Mobile Number"
                                                    />
                                                </div>
                                                <p className="text-danger">{errors.mobile?.message}</p>
                                            </div>

                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Business Type</label>
                                                <select {...register("businessType")} className="form-select">
                                                    <option value="">Please Select</option> 
                                                    <option>Hospital/Laboratory/Polyclinic</option> 
                                                    <option>Hospital Management Software (HMS)</option> 
                                                    <option>Clinic Management Software (CMS)</option> 
                                                    <option>Laboratory Management Software (LIMS)</option> 
                                                    <option>Pharmacy Management Software</option> 
                                                    <option>Medical College & Hospital</option> 
                                                    <option>Consultant</option>
                                                </select>
                                                <p className="text-danger">{errors.businessType?.message}</p>
                                            </div>

                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Email ID</label>
                                                <input
                                                    {...register("email")}
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="Email ID"
                                                />
                                                <p className="text-danger">{errors.email?.message}</p>
                                            </div>

                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Website (If Available)</label>
                                                <input
                                                    {...register("website")}
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Website (If Available)"
                                                />
                                            </div>

                                            <div className="col-12 mb-3">
                                                <label className="form-label">Message</label>
                                                <textarea
                                                    {...register("message")}
                                                    className="form-control"
                                                    rows="2"
                                                    placeholder="Your Message"
                                                ></textarea>
                                            </div>

                                            <div className="col-12">
                                                <button className="btn lanp-btn-primary w-100 text-white rounded-pill" disabled={loading}>
                                                    {loading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>: "Schedule Demo"}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                )}

                                {/* SUCCESS */}
                                {step === "success" && (
                                    <div className="text-center py-5">
                                        <CheckCircle size={60} className="text-success mb-3" />
                                        <h4>Your Demo has been scheduled</h4>
                                        <p>{selectedTime}, {selectedDate.toDateString()}</p>
                                        <p>India Standard Time</p>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BookingModule;